import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { attendanceApi } from '../services/api';

export function RealTimeAttendanceChart() {
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear() % 100);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year % 100, label: year.toString() };
  });

  useEffect(() => {
    setLoading(true);
    attendanceApi.getAttendanceByKVSGrouping(1, selectedYear, selectedMonth).then((response) => {
      if (response?.success && response.data) {
        const dates: string[] = [];
        const regPresent: number[] = [];
        const conPresent: number[] = [];

        const sortedDates = Object.keys(response.data).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        sortedDates.forEach((dateStr) => {
          const dataArray = response.data[dateStr];
          if (dataArray && dataArray.length > 0) {
            const data = dataArray[0];
            const date = new Date(dateStr);
            const formattedDate = `${date.getDate()}/${date.getMonth() + 1}`;
            dates.push(formattedDate);
            regPresent.push(data.totalPresentReg);
            conPresent.push(data.totalPresentCon);
          }
        });

        setChartData({ dates, regPresent, conPresent });
      }
      setLoading(false);
    });
  }, [selectedYear, selectedMonth]);

  if (loading || !chartData) {
    return (
      <div style={{ height: 474, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
          >
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
          >
            {years.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6A1786]"></div>
        </div>
      </div>
    );
  }

  const option = {
    grid: { left: 80, right: 20, top: 80, bottom: 60 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' }
    },
    legend: {
      data: ['Regular Present', 'Contractual Present'],
      top: 10,
      textStyle: { fontSize: 12 }
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: chartData.dates,
      axisLabel: {
        rotate: 45,
        fontSize: 10
      }
    },
    yAxis: {
      type: 'value',
      name: 'No. of Present Staff',
      axisLine: { show: true },
      nameTextStyle: {
        fontSize: 12,
        fontWeight: 'bold'
      }
    },
    series: [
      {
        name: 'Regular Present',
        type: 'line',
        data: chartData.regPresent,
        smooth: true,
        lineStyle: { color: '#FFA500', width: 3 }, // Orange
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(255, 165, 0, 0.5)' }, // Orange
              { offset: 1, color: 'rgba(255, 165, 0, 0.05)' }
            ]
          }
        },
        symbol: 'circle',
        symbolSize: 6
      },
      {
        name: 'Contractual Present',
        type: 'line',
        data: chartData.conPresent,
        smooth: true,
        lineStyle: { color: '#FFDAB9', width: 3 }, // Peach
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(219, 219, 219, 0.7)' }, // Peach
              { offset: 1, color: 'rgb(143, 1, 119, 0.7)' }
            ]
          }
        },
        symbol: 'circle',
        symbolSize: 6
      }
    ]
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: 474 }}>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
        >
          {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
        >
          {years.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
        </select>
      </div>
      <ReactECharts option={option} style={{ flex: 1 }} />
    </div>
  );
}

export function ComparisonChart({ type = 'regular' }: { type?: 'regular' | 'contractual' }) {
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear() % 100);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);


  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year % 100, label: year.toString() };
  });

  useEffect(() => {
    setLoading(true);
    attendanceApi.getAttendanceByKVSGrouping(1, selectedYear, selectedMonth).then((response) => {
      if (response?.success && response.data) {
        const dates: string[] = [];
        const enrollment: number[] = [];
        const present: number[] = [];
        const absent: number[] = [];
        const noResponse: number[] = [];

        const sortedDates = Object.keys(response.data).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        sortedDates.forEach((dateStr) => {
          const dataArray = response.data[dateStr];
          // Only include dates that have data (skip holidays)
          if (dataArray && dataArray.length > 0) {
            const data = dataArray[0];
            const date = new Date(dateStr);
            const formattedDate = `${date.getDate()}/${date.getMonth() + 1}`;
            dates.push(formattedDate);

            if (type === 'regular') {
              enrollment.push(data.totalEnrollmentReg);
              present.push(data.totalPresentReg);
              absent.push(data.totalAbsentReg);
              noResponse.push(data.totalNoResponse);
            } else {
              enrollment.push(data.totalEnrollmentCon);
              present.push(data.totalPresentCon);
              absent.push(data.totalAbsentCon);
              noResponse.push(data.totalNoResponseCon);
            }
          }
        });

        setChartData({ dates, enrollment, present, absent, noResponse });
      }
      setLoading(false);
    });
  }, [selectedYear, selectedMonth, type]);

  if (loading || !chartData) {
    return (
      <div style={{ height: 474, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
          >
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
          >
            {years.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6A1786]"></div>
        </div>
      </div>
    );
  }

  const option = {
    grid: { left: 80, right: 20, top: 80, bottom: 60 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    legend: {
      data: ['Enrollment', 'Present', 'Absent', 'No Response'],
      top: 10,
      textStyle: { fontSize: 12 }
    },
    xAxis: {
      type: 'category',
      data: chartData.dates,
      axisLabel: {
        rotate: 45,
        fontSize: 10
      }
    },
    yAxis: {
      type: 'value',
      name: 'No. of Staff',
      max: 50000,
      axisLine: { show: true },
      nameTextStyle: {
        fontSize: 12,
        fontWeight: 'bold'
      }
    },
    series: [
      {
        name: 'Enrollment',
        type: 'line',
        data: chartData.enrollment,
        lineStyle: { color: '#5470C6', width: 2 },
        symbol: 'circle',
        symbolSize: 6
      },
      {
        name: 'Present',
        type: 'line',
        data: chartData.present,
        lineStyle: { color: '#91CC75', width: 2 },
        symbol: 'circle',
        symbolSize: 6
      },
      {
        name: 'Absent',
        type: 'line',
        data: chartData.absent,
        lineStyle: { color: '#EE6666', width: 2 },
        symbol: 'circle',
        symbolSize: 6
      },
      {
        name: 'No Response',
        type: 'line',
        data: chartData.noResponse,
        lineStyle: { color: '#9A60B4', width: 2, type: 'dashed' },
        symbol: 'triangle',
        symbolSize: 6
      }
    ]
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: 474 }}>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
        >
          {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
        >
          {years.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
        </select>
      </div>
      <ReactECharts option={option} style={{ flex: 1 }} />
    </div>
  );
}

export function InTimePieChart() {
  const option = {
    legend: {
      orient: 'vertical',
      right: '5%',
      top: 'center',
      itemWidth: 25,
      itemHeight: 15,
      itemGap: 15,
      textStyle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#222',
        fontFamily: 'inherit',
      },
      formatter: (name: string) => {
        const maxLength = 20;
        return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
      }
    },
    toolbox: {
      show: true,
      feature: {
        mark: { show: false },
        dataView: { show: false, readOnly: false },
        restore: { show: false },
        saveAsImage: { show: false }
      }
    },
    responsive: true,
    series: [
      {
        name: 'Nightingale Chart',
        type: 'pie',
        radius: [30, 170],
        center: ['30%', '52%'],
        roseType: 'area',
        itemStyle: {
          borderRadius: 4
        },
        data: [
          { value: 26, name: 'Before 9:00am', itemStyle: { color: '#A259F7' } },
          { value: 24.8, name: '9:00am to 9:30am', itemStyle: { color: '#4CB6E6' } },
          { value: 20.3, name: '9:30am to 10:00am', itemStyle: { color: '#F36B21' } },
          { value: 19.5, name: '10:00am to 11:00am', itemStyle: { color: '#1DB954' } },
          { value: 22, name: 'After 11:00am', itemStyle: { color: '#F4C430' } }
        ],
        label: {
          show: true,
          formatter: '{d}%',
          fontSize: 12
        },
      }
    ],
    media: [
      {
        query: { maxWidth: 768 },
        option: {
          legend: {
            orient: 'horizontal',
            bottom: 0,
            left: 'center',
            itemWidth: 20,
            itemHeight: 12,
            itemGap: 10,
            textStyle: {
              fontSize: 11
            }
          },
          series: [{
            radius: ['25%', '55%'],
            center: ['50%', '45%'],
            label: {
              fontSize: 10
            }
          }]
        }
      },
      {
        query: { maxWidth: 480 },
        option: {
          legend: {
            orient: 'horizontal',
            bottom: 0,
            left: 'center',
            itemWidth: 15,
            itemHeight: 10,
            itemGap: 8,
            textStyle: {
              fontSize: 9
            }
          },
          series: [{
            radius: ['20%', '50%'],
            center: ['50%', '40%'],
            label: {
              fontSize: 9
            }
          }]
        }
      }
    ]
  };
  return <ReactECharts option={option} style={{ height: '100%', width: '100%', minHeight: 400 }} opts={{ renderer: 'svg' }} />;
}

export function RegularContractualComparisonChart() {
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear() % 100);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year % 100, label: year.toString() };
  });

  useEffect(() => {
    setLoading(true);
    attendanceApi.getAttendanceByKVSGrouping(1, selectedYear, selectedMonth).then((response) => {
      if (response?.success && response.data) {
        const dates: string[] = [];
        const regPresent: number[] = [];
        const regAbsent: number[] = [];
        const conPresent: number[] = [];
        const conAbsent: number[] = [];

        const sortedDates = Object.keys(response.data).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        sortedDates.forEach((dateStr) => {
          const dataArray = response.data[dateStr];
          // Only include dates that have data (skip holidays)
          if (dataArray && dataArray.length > 0) {
            const data = dataArray[0];
            const date = new Date(dateStr);
            const formattedDate = `${date.getDate()}/${date.getMonth() + 1}`;
            dates.push(formattedDate);
            regPresent.push(data.totalPresentReg);
            regAbsent.push(data.totalAbsentReg);
            conPresent.push(data.totalPresentCon);
            conAbsent.push(data.totalAbsentCon);
          }
        });

        setChartData({ dates, regPresent, regAbsent, conPresent, conAbsent });
      }
      setLoading(false);
    });
  }, [selectedYear, selectedMonth]);

  if (loading || !chartData) {
    return (
      <div style={{ height: 474, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
          >
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
          >
            {years.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6A1786]"></div>
        </div>
      </div>
    );
  }

  const option = {
    grid: { left: 80, right: 20, top: 80, bottom: 60 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' }
    },
    legend: {
      data: ['Regular Present', 'Regular Absent', 'Contractual Present', 'Contractual Absent'],
      top: 10,
      textStyle: { fontSize: 12 }
    },
    xAxis: {
      type: 'category',
      data: chartData.dates,
      axisLabel: {
        rotate: 45,
        fontSize: 10
      }
    },
    yAxis: {
      type: 'value',
      name: 'No. of Staff',
      axisLine: { show: true },
      nameTextStyle: {
        fontSize: 12,
        fontWeight: 'bold'
      }
    },
    series: [
      {
        name: 'Regular Present',
        type: 'line',
        data: chartData.regPresent,
        lineStyle: { color: '#91CC75', width: 2 },
        symbol: 'circle',
        symbolSize: 6
      },
      {
        name: 'Regular Absent',
        type: 'line',
        data: chartData.regAbsent,
        lineStyle: { color: '#EE6666', width: 2 },
        symbol: 'circle',
        symbolSize: 6
      },
      {
        name: 'Contractual Present',
        type: 'line',
        data: chartData.conPresent,
        lineStyle: { color: '#73C0DE', width: 2 },
        symbol: 'diamond',
        symbolSize: 6
      },
      {
        name: 'Contractual Absent',
        type: 'line',
        data: chartData.conAbsent,
        lineStyle: { color: '#FC8452', width: 2 },
        symbol: 'diamond',
        symbolSize: 6
      }
    ]
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: 474 }}>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
        >
          {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786]"
        >
          {years.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
        </select>
      </div>
      <ReactECharts option={option} style={{ flex: 1 }} />
    </div>
  );
}

// export function TodaysTrendChart() {) {
//   const option = {
//     grid: { left: 60, right: 0, top: 60, bottom: 40 },
//     tooltip: { trigger: 'axis' },
//     xAxis: {
//       type: 'category',
//       data: ['8:00', '9:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00']
//     },
//     yAxis: {
//       type: 'value',
//       axisLine: { show: true }
//     },
//     series: [
//       {
//         data: [100, 200, 300, 400, 350, 300, 250, 200, 150, 100],
//         type: 'line',
//         areaStyle: {
//           color: {
//             type: 'linear',
//             x: 0,
//             y: 0,
//             x2: 0,
//             y2: 1,
//             colorStops: [
//               {
//                 offset: 0,
//                 color: '#E0CE00' // top color
//               },
//               {
//                 offset: 1,
//                 color: '#FFF' // bottom color
//               }
//             ]
//           },
//           opacity: 1
//         },
//         lineStyle: { color: '#9F930B', width: 3 },
//         symbol: 'none',
//         smooth: true
//       }
//     ]
//   };
//   return <ReactECharts option={option} style={{ height: 474 }} />;
// }
